import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * ── Auth draft: the in-progress details being entered in the flow ─────────
 *
 * The auth flow is four screens, each its own route (`app/auth/*`). Because
 * every step is a separate route, per-screen `useState` can't carry the
 * mobile/name/email forward — this context is the one thing that spans the step
 * routes. It's TRANSIENT: mounted in `app/auth/_layout.tsx` (scoped to the flow),
 * so it auto-discards when the modal is dismissed.
 *
 * What it holds is everything that is NOT yet server truth: what the user has
 * typed, and the OTP challenges in flight. The moment the server hands back a
 * real user, that goes to the long-lived [[session]] instead — so an abandoned
 * flow leaves nothing behind here, while a completed step is already durable
 * over there.
 *
 * ── The challenge ids ──
 * An OTP is confirmed by `(challenge_id, otp)`, so the id issued by the screen
 * that SENT the code has to reach the screen that verifies it — a different
 * route. Mobile and email are separate fields deliberately: both challenges are
 * alive during the flow, and one shared field would let a mobile resend clobber
 * the email one.
 *
 * They are REPLACED, not just set: every `resendOtp` destroys the previous
 * challenge and returns a new id, so the verify screens overwrite on each resend.
 * Holding a stale id makes the next verify fail for no visible reason.
 *
 * ── Why it also tracks OTP "sends" ──
 * The resend countdown ([[useResendCountdown]]) must NOT restart every time an
 * OTP screen is shown. Editing a prior step is done by going BACK (the native
 * back button) — which unmounts the OTP screen — and returning. On return:
 *   - value UNCHANGED → no new OTP was sent → the countdown must RESUME where it
 *     was (not reset to the full duration).
 *   - value CHANGED → a new OTP went out → the countdown restarts.
 * That decision needs state that (a) survives the screen unmounting and (b)
 * remembers WHAT value the last OTP was sent for. Per-screen state can't; the
 * draft can. So we keep, per target (mobile / email), the value the last code was
 * sent for and when its countdown started.
 *
 * `markSent` is called by whichever screen actually caused a code to go out — the
 * one that issued it (mobile, details) or the resend button on an OTP screen. It
 * is deliberately NOT called from an OTP screen's mount: a send is a real network
 * event, and stamping it on mount would start a countdown for a code that was
 * never dispatched.
 * ─────────────────────────────────────────────────────────────────────────
 */

/** One OTP "send": the value it went to, and when the countdown started. */
type OtpSend = {
  sentForValue: string;
  /** `Date.now()` at send time — the countdown is computed against this. */
  startedAt: number;
  challengeId: string;
};

export type OtpTarget = 'mobile' | 'email';

type AuthDraftContextValue = {
  /** The BARE 10 digits — no `+91`. Callers prepend `DIAL_CODE` for the API. */
  mobile: string;
  firstName: string;
  /** Optional — a blank last name still completes the profile. */
  lastName: string;
  email: string;
  setMobile: (v: string) => void;
  setFirstName: (v: string) => void;
  setLastName: (v: string) => void;
  setEmail: (v: string) => void;
  /** Challenge for the SMS code. Replaced on every resend (see docstring). */
  /** Challenge for the emailed code. Replaced on every resend. */
  /** Per-target OTP send-tracking (see docstring). */
  otpSend: { mobile?: OtpSend; email?: OtpSend };
  /**
   * Record that a code WAS dispatched to `value` for `target`, stamping the
   * moment of the call. Callers don't pass a time — a send is always happening
   * now, and letting one be backdated would only ever be a bug.
   */
  markSent: (target: OtpTarget, value: string, challengeId: string) => void;
  /** Clear the whole draft (a dev reset). */
  reset: () => void;
};

const AuthDraftContext = createContext<AuthDraftContextValue | null>(null);

/**
 * Owns the transient auth draft. Mount inside `app/auth/_layout.tsx` so it's
 * scoped to the flow and discarded when the modal closes.
 */
export function AuthContextProvider({ children }: { children: ReactNode }) {
  const [mobile, setMobile] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  const [otpSend, setOtpSend] = useState<{
    mobile?: OtpSend;
    email?: OtpSend;
  }>({});

  const markSent = useCallback(
    (target: OtpTarget, value: string, challengeId: string) => {
      setOtpSend((prev) =>
        prev[target]?.challengeId === challengeId
          ? prev
          : {
              ...prev,
              [target]: {
                sentForValue: value,
                startedAt: Date.now(),
                challengeId,
              },
            }
      );
    },
    []
  );

  const reset = useCallback(() => {
    setMobile('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setOtpSend({});
  }, []);

  const value = useMemo<AuthDraftContextValue>(
    () => ({
      mobile,
      firstName,
      lastName,
      email,
      setMobile,
      setFirstName,
      setLastName,
      setEmail,
      otpSend,
      markSent,
      reset,
    }),
    [mobile, firstName, lastName, email, otpSend, markSent, reset]
  );

  return (
    <AuthDraftContext.Provider value={value}>
      {children}
    </AuthDraftContext.Provider>
  );
}

export function useAuthContext(): AuthDraftContextValue {
  const ctx = useContext(AuthDraftContext);
  if (!ctx) {
    throw new Error(
      'useAuthDraft must be used inside <AuthDraftProvider> (mounted in app/auth/_layout.tsx).'
    );
  }
  return ctx;
}
